import { z } from "zod";

import { MemorySchemaCardinalitySchema, MemorySchemaPadNameSchema } from "./memory-schema-pad";

/**
 * The BUILDER-facing declaration surface: what keys a scope permits.
 *
 * ## 🔴 WITHOUT THIS DOOR THE WHOLE AGENT-MEMORY FEATURE IS INERT, AND THAT IS MEASURED
 *
 * `assertKeyIsDeclared` resolves the ACTIVE `MemorySchema` for `(organizationId, scope)`
 * and throws `AgentMemorySchemaNotActivatedError` when there is none. Nothing in the
 * product could create a `MemorySchema` row: the only non-test reader of the
 * `memorySchema` delegate was that `findFirst`, and the only writer was an integration
 * spec. Production on 2026-08-31 held **0 MemorySchema rows, 0 activated, 0
 * AgentMemoryEntry, 0 AgentMemory** against a `Workflow` control of 1,041 — so every
 * write the shipped door could have taken was refused, and the model-facing tool and the
 * prompt block were reachable and could never have anything to show.
 *
 * ## ⚠️ THE WIRE CARRIES THE FIELD SHAPE; THE STORED SHAPE IS STILL THE BACKEND'S
 *
 * `MemorySchema.definition` is `Json`, and its authority is
 * `agent-memory/domain/value-objects/memory-schema-definition.ts`. This contract declares
 * what a CALLER sends and reads back; the value object declares what is STORED and is the
 * only thing that parses the column. The two agree on the field names below and differ in
 * exactly one way that matters: the value object's stored shape is LENIENT (every field
 * but `key` is optional, so a declaration written by an older version still parses),
 * while this wire shape is STRICT, because a caller writing a declaration today has no
 * excuse for omitting a description.
 *
 * 🔴 THE ONE CHECK THAT CANNOT LIVE HERE IS THE DEGENERATE-DESCRIPTION REFUSAL.
 * `isDegenerateDescription` lives in `apps/backend/src/_common/utils/degenerate-description.util.ts`,
 * and `@nexus/types` cannot import from `apps/backend`. So this schema bounds a
 * description's LENGTH and the backend refuses one that says nothing — see
 * `memorySchemaDeclarationWriteSchemaFor`. A second copy of that predicate here would be
 * a looser copy of a rule, which is a bypass rather than a duplicate.
 */

/**
 * `MemorySchema.scope`, whose CHECK is `MemorySchema_scope_shape` —
 * `CHECK ("scope" ~ '^[a-z][a-z0-9_]*$')`, read out of
 * `20260827190000_add_agent_memory/migration.sql`.
 *
 * 🔴 THIS IS NOT `AgentMemoryScopeSchema`, AND CONFLATING THEM WOULD BE A REAL BUG IN
 * BOTH DIRECTIONS. `AgentMemory.scope` pins MEMBERSHIP — `AgentMemory_scope_matches_arm`
 * enumerates `chat`/`customer`/`agent` by name, because each is a real foreign-key arm.
 * `MemorySchema.scope` pins only FORM: the catalog is a const union in `@nexus/types`
 * designed to grow without an `ALTER TYPE`, so the database and the catalog disagree BY
 * DESIGN and a retired-but-well-formed value still passes the CHECK. `core.ts` says the
 * same thing from the other side.
 *
 * Narrowing this to the three-value enum would refuse a scope the database accepts and
 * that a future arm needs; widening `AgentMemory.scope` to this regex would let a write
 * name an arm that has no column.
 *
 * ⚠️ AN EXACT MIRROR, NEVER A TIGHTENING — the same rule the key shape carries. A guard
 * stricter than its column costs a 400 the database would have taken; a guard LOOSER
 * than its column lets the value reach Postgres and come back as an unmapped SQLSTATE
 * 23514 rendered as a 500. The `^`/`$` anchors are the pattern's own, so Postgres's
 * unanchored `~` and JavaScript's `test` agree, and neither lets `$` match before a
 * trailing newline without a flag neither side passes.
 */
export const MEMORY_SCHEMA_SCOPE_SHAPE = /^[a-z][a-z0-9_]*$/;

export const MemorySchemaScopeSchema = z.string().min(1).max(64).regex(MEMORY_SCHEMA_SCOPE_SHAPE);

/**
 * What a declared field's value looks like, so a consumer can build a JSON-Schema
 * property, a Zod validator, a `{{memory.*}}` symbol and a field picker from ONE row.
 *
 * 🔴 `choice` CARRIES NO OPTION LIST YET AND THAT IS A KNOWN CEILING, NOT AN OVERSIGHT.
 * A `choice` therefore behaves exactly like `text` at every consumer today. It is
 * declared anyway because the set is what a builder UI offers and a member added later
 * is a wire-breaking change, where a member declared early and unused is not. The
 * `debt:` marker on the stored value object names the trigger that closes it.
 *
 * ⚠️ NO `object` AND NO `array`. `AgentMemoryEntry.value` is `TEXT` — deliberately, so
 * `octet_length(value)` and `Buffer.byteLength(value, "utf8")` agree BY CONSTRUCTION —
 * so every one of these is a scalar rendered into a string. A composite type would have
 * to serialise, and the charge the write door computes would then measure a
 * serialisation nobody chose.
 */
export const MEMORY_SCHEMA_FIELD_TYPES = ["text", "number", "boolean", "date", "choice"] as const;

export const MemorySchemaFieldTypeSchema = z.enum(MEMORY_SCHEMA_FIELD_TYPES);

export type MemorySchemaFieldType = z.infer<typeof MemorySchemaFieldTypeSchema>;

/**
 * The longest description a builder may write for one field.
 *
 * 🔴 IT IS BELOW THE MODEL-FACING TRUNCATION POINT ON PURPOSE, AND THAT IS THE WHOLE
 * REASON FOR THE NUMBER. `sanitizeModelFacingText` (backend,
 * `tools/schema-validation/model-facing-text.ts`) cuts model-facing text at
 * `MODEL_FACING_TEXT_MAX_CHARS = 1024` characters. A declaration bounded ABOVE that would
 * let a builder write a description the model never finishes reading, with nothing
 * anywhere saying so — the same "what was saved is not what was shown" defect the render
 * budget already had once. Bounded below it, what a builder writes is what the model
 * reads, with room for the framing a tool definition adds around it.
 */
export const MEMORY_SCHEMA_FIELD_DESCRIPTION_MAX_CHARS = 500;

/**
 * One declared field, as a caller writes it and as a builder reads it back.
 *
 * 🔴 `description` IS REQUIRED AND `.min(1)`, AND IT IS THE FIELD THIS SHAPE EXISTS FOR.
 * It is what the agent is told the field MEANS — it becomes the `description` on the
 * memory tool's `agentInputSchema` property, and a model choosing what to write into
 * `customer_name` has nothing else to go on. Measured on production 2026-08-31 across the
 * neighbouring tool-config surface: **135 of 781 configs (17.3%) carry a description that
 * tells a model nothing beyond the name it already has**, and on WORKFLOW configs alone
 * it is 107 of 422 (25.4%) — see `build-agent-tool-parameters.ts`'s own docblock for the
 * census and its stated floor. Requiring it at the DECLARATION is the only point in the
 * chain where that cannot rot, because every later reader is downstream of a stored row.
 *
 * ⚠️ NON-EMPTY IS NOT THE SAME BAR AS NON-DEGENERATE, AND THIS SCHEMA ONLY REACHES THE
 * FIRST. A description equal to its own label passes every length check ever written and
 * carries exactly zero information. The backend refuses that; see this file's header for
 * why the predicate cannot live here.
 *
 * `scope` is the SUBJECT the field belongs to, and it must equal the declaration row's
 * own `scope` — the backend refuses a field that disagrees, naming the index. It is
 * carried per field rather than left implicit because the downstream consumers read a
 * FIELD, not a row: a `{{memory.*}}` symbol table and a branch-node field picker both
 * need to know which subject a name resolves against, and a rule they each re-derive from
 * the enclosing row is a rule one of them gets wrong.
 *
 * 🔴 IT IS THE FORM CHECK, NOT `AgentMemoryScopeSchema`. Narrowing it to the three-arm
 * enum would refuse a scope `MemorySchema_scope_shape` accepts and a future arm needs —
 * the same argument {@link MEMORY_SCHEMA_SCOPE_SHAPE} carries one screen up, and the
 * equality check against the row's scope is what actually constrains this field.
 */
export const MemorySchemaFieldSchema = z.object({
  /**
   * ⚠️ A BARE `z.string()`, AND THAT IS DELIBERATE RATHER THAN AN OMISSION. The key must
   * satisfy `AgentMemoryEntry_key_shape` and `AgentMemoryEntry_key_bounded`, and the ONE
   * mirror of those CHECKs is `StoredKeySchema` in the backend value object
   * (`memory-schema-definition.ts`), which both the stored shape and the write shape
   * consult and whose spec reads the pattern out of the migration itself. A second copy
   * of that regex here would be two copies agreeing with each other while the database
   * holds a third.
   */
  key: z.string(),
  /** Human-facing. Shown in a builder UI; never sent to a model as the field's meaning. */
  label: z.string().min(1).max(128),
  type: MemorySchemaFieldTypeSchema,
  /** What the AGENT is told this field means. Required — see the docblock above. */
  description: z.string().min(1).max(MEMORY_SCHEMA_FIELD_DESCRIPTION_MAX_CHARS),
  scope: MemorySchemaScopeSchema,
  /**
   * Whether a subject's memory is INCOMPLETE while this field is unset.
   *
   * ⚠️ IT IS A DECLARATION, NOT AN ENFORCEMENT. Nothing refuses a write for a missing
   * required field and nothing should: the entries arrive one at a time from an agent
   * turn, so "not yet set" is the normal state of every field for most of a
   * conversation. It exists so a consumer can ask the agent to go and find the rest.
   *
   * Defaulted rather than required, because `false` is the only answer a caller who did
   * not think about it can mean.
   */
  requiredForCompletion: z.boolean().default(false)
});

export type MemorySchemaField = z.infer<typeof MemorySchemaFieldSchema>;

/**
 * The same field as a caller READS IT BACK — identical in every respect except that
 * `description` may be empty.
 *
 * 🔴 THE WRITE SHAPE CANNOT BE REUSED HERE, AND REUSING IT WAS A REAL DEFECT. The stored
 * reader fills an absent description with `""` for any declaration written before the
 * per-field widening, and `MemorySchema` is append-only, so those rows are the normal
 * case rather than an edge one. With `.min(1)` on this arm the GENERATED CLIENT's
 * `ZAgentMemory.ActivateSchema.Response.parse(response)` throws on them — **after the
 * server has already committed the activation**, so the transition happened and the
 * caller sees only an error. Found by review on #4917.
 *
 * 🔑 THE GENERAL SHAPE, WHICH THIS FILE ALREADY ARGUES ONE FIELD OVER: a contract more
 * precise than its producer does not remove the gap, it MOVES it — here out of the server
 * and into the client's parse, which is the one place nothing can catch it. The producer
 * is `memorySchemaDefinitionSchemaFor`, whose description is `.catch("")`; this is what
 * that produces.
 *
 * ⚠️ `""` IS NOT "NO DESCRIPTION SUPPLIED BY A CALLER" — it is *"declared before
 * descriptions were required, and nobody has said what it means yet"*. The write door
 * refuses an empty one, so no new declaration can create it.
 */
export const MemorySchemaFieldViewSchema = MemorySchemaFieldSchema.extend({
  description: z.string().max(MEMORY_SCHEMA_FIELD_DESCRIPTION_MAX_CHARS)
});

export type MemorySchemaFieldView = z.infer<typeof MemorySchemaFieldViewSchema>;

/**
 * Declare a new version of a scope's field list.
 *
 * 🔴 `keys` IS `.min(1)`, AND AN EMPTY DECLARATION IS REFUSED RATHER THAN STORED.
 * `AgentMemorySchemaNotActivatedError`'s own header argues that a `{ keys: [] }` default
 * is *worse* than refusing, because it then rejects every write as "undeclared key" while
 * an operator reading the row sees a configured scope and nothing anywhere names the real
 * cause. A declaration that could never authorise anything is that same footgun arriving
 * through the front door, so it is refused here where the caller can see it.
 *
 * 🔴 DUPLICATES ARE REFUSED TOO. `assertKeyIsDeclared` tests membership with `.some()`,
 * so a repeated key changes no behaviour whatever — which is exactly why it must not be
 * stored: a reader comparing a declaration's length against its distinct keys would find
 * a difference that means nothing, and the day a per-key cap lands (the value object's
 * `debt:` trigger) two rows for one key have no defined answer.
 *
 * ⚠️ NO `version` FIELD, DELIBERATELY. Rows are APPEND-ONLY — a new declaration is a new
 * row so that entries written under an older one are not retroactively invalidated — and
 * the version is therefore the server's to allocate, not the caller's. A caller-supplied
 * version is a lost-update race with a nicer name.
 */
export const MemorySchemaDeclareBodySchema = z.object({
  scope: MemorySchemaScopeSchema,
  /**
   * The PAD this declaration belongs to — see {@link MemorySchemaPadNameSchema}.
   *
   * 🔴 REQUIRED, NEVER DEFAULTED, AND THAT IS THE ONE CALL WORTH ARGUING. A default
   * would put every declaration a caller did not think about into one shared pad, which
   * is the exact org-global shape this axis exists to end — and it would do it silently,
   * so the first sign would be two agents advertising each other's fields. The migration
   * DOES backfill pre-existing rows to a pad called `default`, because a row already
   * written cannot be asked what it meant; a caller writing one today can be.
   *
   * ⚠️ VERSIONS ARE ALLOCATED PER PAD. `@@unique([organizationId, scope, name, version])`
   * — so declaring into a new pad starts at version 1 regardless of how long the scope's
   * other pads have existed, and two pads' version numbers say nothing about each other.
   */
  name: MemorySchemaPadNameSchema,
  /**
   * ⚠️ DEFAULTED, WHERE `name` IS NOT, AND THE TWO ARE NOT INCONSISTENT. `single` is what
   * the write door does today for BOTH values, so a caller who did not think about it
   * gets the behaviour they are already getting; there is no silent partition to fall
   * into. See {@link MemorySchemaCardinalitySchema} for what `multiple` does not yet do.
   */
  cardinality: MemorySchemaCardinalitySchema.default("single"),
  keys: z
    .array(MemorySchemaFieldSchema)
    .min(1)
    .refine((keys) => new Set(keys.map((field) => field.key)).size === keys.length, {
      message: "keys must be unique — a repeated key changes nothing and cannot be interpreted"
    })
});

export type MemorySchemaDeclareBody = z.infer<typeof MemorySchemaDeclareBodySchema>;

/**
 * The id of one stored declaration.
 *
 * `MemorySchema.id` is `@db.Uuid`, so a non-canonical id reaches Postgres as SQLSTATE
 * 22P02 — which `PrismaClientExceptionFilter` does not map, and a malformed REQUEST then
 * renders as a 500. Refusing it here makes it the 400 it is. Same argument as
 * `AgentMemoryReadPathVarsSchema`.
 */
export const MemorySchemaPathVarsSchema = z.object({
  memorySchemaId: z.string().uuid()
});

/**
 * One stored declaration, as a builder reads it back.
 *
 * `activatedAt` is `null` for every version that is not the live one. At most ONE version
 * per `(organizationId, scope)` can be non-null at a time and that is a database
 * guarantee, not a convention: `MemorySchema_one_active` is a partial unique index
 * (`WHERE "activatedAt" IS NOT NULL`) which Prisma cannot express and which is therefore
 * absent from `schema.prisma`.
 *
 * ⚠️ `keys` IS PROJECTED FROM `definition`, NOT THE COLUMN ITSELF. The column is `Json`
 * and its shape is the backend value object's; serving the raw column would put an
 * unvalidated document on the wire and make every future widening a breaking change for
 * whoever parsed it loosely. The projection is what the door promises.
 *
 * 🔴 IT IS TOTAL EVEN OVER A DECLARATION STORED BEFORE THESE FIELDS EXISTED. The value
 * object's stored shape is lenient and fills every absent field on read — `label` from
 * the key, `scope` from the row's own scope, `type` `text`, `description` `""`,
 * `requiredForCompletion` `false` — so a caller reading this back never sees an
 * `undefined` it has to interpret. `description: ""` is the one value that means
 * something on its own: that field was declared before descriptions were required, and
 * nobody has said what it means yet.
 *
 * 🔴 WHICH IS EXACTLY WHY THIS ARM IS {@link MemorySchemaFieldViewSchema} AND NOT THE
 * WRITE SHAPE. The write shape's `description` is `.min(1)`; a legacy row's is `""`. See
 * that schema's docblock for what reusing the strict one did to the generated client.
 *
 * ⚠️ THE PROPERTY IS STILL CALLED `keys` AND THAT IS NOT AN OVERSIGHT. It is the property
 * name inside the STORED `definition` document, which is append-only: rows written before
 * this widening carry `keys`, so renaming it on the wire alone would leave two names for
 * one thing and a reconciliation for every reader to perform.
 */
export const MemorySchemaVersionViewSchema = z.object({
  id: z.string(),
  scope: z.string(),
  /**
   * The pad this version declares.
   *
   * ⚠️ `z.string()`, matching `scope` beside it and NOT the form check the write body
   * used. A read arm stricter than its producer moves the failure into the generated
   * client's `Response.parse`, which is the one place nothing catches it — see
   * {@link MemorySchemaFieldViewSchema} for what that already cost once on this very
   * contract. The migration backfills pre-axis rows to `default`, and `default` passes the
   * form check anyway; the point is that no read arm here re-validates a producer.
   */
  name: z.string(),
  /**
   * ⚠️ `z.string()` FOR THE SAME REASON, EVEN THOUGH THE COLUMN'S CHECK PINS MEMBERSHIP.
   * `MemorySchema_cardinality_membership` is a database guarantee and this is a read arm;
   * narrowing it to the enum would make a value the database accepted unparseable by the
   * client that asked for it, after the server had already answered.
   */
  cardinality: z.string(),
  version: z.number().int().positive(),
  keys: z.array(MemorySchemaFieldViewSchema),
  /** ISO-8601, or `null` when this version is not the active one. */
  activatedAt: z.string().nullable(),
  createdAt: z.string()
});

export type MemorySchemaVersionView = z.infer<typeof MemorySchemaVersionViewSchema>;

/**
 * Which scope's declarations to read back.
 *
 * ⚠️ THE ONLY QUERY PARAMETER, AND THE SCOPE IS REQUIRED RATHER THAN OPTIONAL. A read
 * with no scope would have to mean *"every scope's declarations"*, which is a different
 * question with a different answer shape — one active row PER scope rather than one —
 * and nothing asks it. Requiring the scope keeps this route answering exactly one
 * question, and a caller that wants two scopes makes two requests it can cache
 * separately.
 *
 * 🔴 IT IS {@link MemorySchemaScopeSchema}, THE FORM CHECK, FOR THE SAME REASON THE WRITE
 * BODY USES IT: an exact mirror of `MemorySchema_scope_shape`, never the three-arm
 * `AgentMemoryScopeSchema`. Narrowing it here would refuse a scope the database accepts
 * and that a declaration may already exist under — a read that cannot see a row the write
 * door was allowed to create.
 */
export const MemorySchemaListParamsSchema = z.object({
  scope: MemorySchemaScopeSchema
});

export type MemorySchemaListParams = z.infer<typeof MemorySchemaListParamsSchema>;

/**
 * How many versions of one scope this route serves, newest first.
 *
 * `MemorySchema` is APPEND-ONLY — every edit to a scope's field list is a new row — so a
 * long-lived organisation's history grows without bound and an unbounded response is not
 * a shape this route may promise. The window is fixed rather than caller-supplied
 * because a builder reads the newest few and a knob is wire surface nobody has asked
 * for.
 *
 * 🔴 THE TRUNCATION IS VISIBLE, WHICH IS THE WHOLE REASON A FIXED WINDOW IS HONEST HERE.
 * `totalVersions` on the response is a real `COUNT` over the same predicate — never
 * `versions.length` — so a caller can always tell that older versions exist. A `total`
 * computed after a `take` reads as a complete collection and permanently hides whatever
 * sits past it; that is the defect this constant exists to avoid rather than repeat.
 *
 * debt: only the newest MEMORY_SCHEMA_VERSION_WINDOW versions of a scope are reachable
 *       through this route; older rows are counted and cannot be fetched.
 *       Upgrade trigger: totalVersions exceeds this window for any scope in production —
 *       the first moment an operator needs a version this route cannot return.
 */
export const MEMORY_SCHEMA_VERSION_WINDOW = 20;

/**
 * Every declaration one scope has, and which of them is live.
 *
 * ## 🔴 `active` IS ITS OWN FIELD AND MUST NOT BE DERIVED FROM `versions`
 *
 * Scanning the list for `activatedAt !== null` is wrong twice over. Activation is not
 * ordered with version allocation — an operator may declare v5 and leave v4 live, or
 * re-activate an old version — so the live row is not reliably the newest; and the
 * window above means it can sit outside `versions` entirely. Serving it separately makes
 * the answer total regardless of how long a scope's history is.
 *
 * ⚠️ SO THE LIVE VERSION APPEARS TWICE WHEN IT IS INSIDE THE WINDOW, AND THAT IS
 * DELIBERATE. Both copies are read in ONE database transaction, so they cannot disagree
 * — without that, a concurrent activation would put one id on the wire twice with
 * contradictory `activatedAt` values.
 *
 * 🔴 `active` IS `null` WHEN THE SCOPE HAS NO LIVE DECLARATION, AND THAT IS THE STATE
 * THAT MATTERS MOST. `assertKeyIsDeclared` refuses every agent-memory write for a scope
 * in exactly that state, with `AgentMemorySchemaNotActivatedError`. It is also what a
 * scope with no rows at all looks like — `versions` is then empty and `totalVersions` is
 * `0`, which is what tells the two apart.
 *
 * ⚠️ `active.keys` IS THE LIST A BUILDER MUST PRE-POPULATE AN EDIT FROM.
 * `MemorySchemaDeclareBodySchema.keys` is the WHOLE field list and activating a version
 * stands the live one down, so an edit is a full re-declaration: a field left out of the
 * next declaration stops being declared, and every agent write to it is refused
 * afterwards with nothing at the edit site saying so.
 */
export const MemorySchemaPadDeclarationsSchema = z.object({
  /** The pad's name — see {@link MemorySchemaPadNameSchema}. */
  name: z.string(),
  active: MemorySchemaVersionViewSchema.nullable(),
  /** Newest first, at most {@link MEMORY_SCHEMA_VERSION_WINDOW}. */
  versions: z.array(MemorySchemaVersionViewSchema),
  /**
   * A real `COUNT` over every version of this PAD, not `versions.length`.
   *
   * `totalVersions > versions.length` is the only signal that the window truncated the
   * list, and it is the field that keeps the truncation honest.
   */
  totalVersions: z.number().int().nonnegative()
});

export type MemorySchemaPadDeclarations = z.infer<typeof MemorySchemaPadDeclarationsSchema>;

/**
 * How many pads of one scope this route serves.
 *
 * The same honesty the version window already carries, one level up: {@link totalPads} is
 * a real `COUNT` over the scope's distinct pads, so a caller can always tell that more
 * exist. A pad count is small by nature — a pad is a field group an agent is granted, not
 * a per-record thing — so this window is expected never to bite; it exists because
 * "expected never to bite" is not a bound.
 */
export const MEMORY_SCHEMA_PAD_WINDOW = 50;

export const MemorySchemaScopeDeclarationsSchema = z.object({
  /**
   * Echoed from the request, so a cached response carries the question it answers.
   *
   * ⚠️ `z.string()`, matching {@link MemorySchemaVersionViewSchema}'s own `scope` and NOT
   * the form check the request used. A read arm stricter than its producer moves the
   * failure into the generated client's `Response.parse`, which is the one place nothing
   * catches it — see {@link MemorySchemaFieldViewSchema} for what that already cost once.
   */
  scope: z.string(),
  /**
   * Every pad on this scope, name-ascending, at most {@link MEMORY_SCHEMA_PAD_WINDOW}.
   *
   * 🔴 ONE ROUTE AND ONE PORT READ, NOT A PAD LIST BESIDE A PER-PAD DETAIL ROUTE. This
   * contract's own header already argues it for `active` versus `versions`: both copies
   * are read in ONE database transaction, so they cannot disagree. Split across two
   * requests, a pad that appears active in one answer and inactive in the other is
   * indistinguishable from a concurrent activation — and with pads there are now N
   * activations racing instead of one.
   *
   * ⚠️ A PAD WITH NO ACTIVE VERSION STILL APPEARS, WITH `active: null`. That is the state
   * `assertKeyIsDeclared` refuses every write for, so hiding it would hide the one thing
   * an operator needs to see. It is also what a pad whose only versions sit outside the
   * version window looks like, which `totalVersions` is what tells apart.
   */
  pads: z.array(MemorySchemaPadDeclarationsSchema),
  /**
   * A real `COUNT DISTINCT` over this scope's pad names, not `pads.length`.
   *
   * `totalPads > pads.length` is the only signal that the pad window truncated the list.
   */
  totalPads: z.number().int().nonnegative()
});

export type MemorySchemaScopeDeclarations = z.infer<typeof MemorySchemaScopeDeclarationsSchema>;
